import { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { WizardTabs } from './step-indicator'
import { AgentPreview } from './agent-preview'
import { AgentPlayground } from './agent-playground'
import { StepTemplateSelect } from './steps/step-template-select'
import { StepBasicInfo } from './steps/step-basic-info'
import { StepLLMConfig } from './steps/step-llm-config'
import { StepSystemPrompt } from './steps/step-system-prompt'
import { StepTools } from './steps/step-tools'
import { StepKnowledge } from './steps/step-knowledge'
import { StepAtendimento } from './steps/step-atendimento'
import { StepAdvanced } from './steps/step-advanced'
import { useAgentWizardStore } from '@/stores/agent-wizard-store'
import { useProjectStore } from '@/stores/project-store'
import { useOrganizationStore } from '@/stores/organization-store'
import { useAtendimento } from '@/hooks/use-atendimento'
import { agentService, type AgentCreateRequest, type AgentUpdateRequest } from '@/services/agent'
import {
  agentWizardSchema,
  agentWizardDefaults,
  WIZARD_STEPS,
  type AgentWizardData,
} from '@/schemas/agent-wizard'
import { ArrowLeft, ArrowRight, Check, Loader2, X, Eye, Play, Save } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AxiosError } from 'axios'
import type { Agent } from '@/schemas/agent'

interface AgentWizardProps {
  onSuccess?: (agentId: string) => void
  onCancel?: () => void
  editMode?: boolean
  agentId?: string
  initialShowPlayground?: boolean
}

function agentToWizardData(agent: Agent): Partial<AgentWizardData> {
  return {
    name: agent.name,
    description: agent.description ?? undefined,
    provider: agent.llm_config?.provider ?? undefined,
    model: agent.llm_config?.model ?? undefined,
    temperature: agent.llm_config?.temperature ?? 0.7,
    max_tokens: agent.llm_config?.max_tokens ?? 2048,
    api_key_id: agent.llm_config?.api_key_id ?? undefined,
    system_prompt: agent.system_prompt ?? undefined,
    tool_ids: agent.tools?.map((t) => t.tool_id) ?? [],
    knowledge_base_ids: agent.knowledge_bases?.map((kb) => kb.knowledge_base_id) ?? [],
    memory_enabled: agent.memory_config?.enabled ?? true,
    memory_type: agent.memory_config?.type ?? 'short_term',
    memory_scope: agent.memory_config?.scope ?? 'conversation',
    memory_context_window: agent.memory_config?.context_window ?? 20,
    memory_retention_days: agent.memory_config?.retention_days ?? undefined,
    memory_auto_summarize: agent.memory_config?.auto_summarize ?? false,
    memory_max_tokens: agent.memory_config?.max_tokens ?? undefined,
    memory_window_size: agent.memory_config?.window_size ?? undefined,
    timeout: agent.settings?.timeout ?? 60,
    max_retries: agent.settings?.max_retries ?? 3,
    max_iterations: agent.settings?.max_iterations ?? 10,
    is_template: agent.is_template,
    is_active: agent.is_active,
  }
}

export function AgentWizard({
  onSuccess,
  onCancel,
  editMode = false,
  agentId,
  initialShowPlayground = false,
}: AgentWizardProps) {
  const { t } = useTranslation('agents')
  const navigate = useNavigate()
  // `?step=7` ABRE O PASSO PEDIDO (spec 157): é o link "Abrir atendimento" da
  // configuração da organização. Vale uma vez, depois que o agente carregou.
  const [searchParams] = useSearchParams()
  const passoPedido = Number(searchParams.get('step'))
  const selectedProject = useProjectStore((state) => state.selectedProject)
  const { currentOrganization } = useOrganizationStore()
  // O PASSO ATENDIMENTO APARECE PELA MESMA FONTE QUE A CHAVE GRAVA, e não por
  // estado local: assim a aba não pode discordar do que o servidor guarda.
  const { atendimentoDe, recarregar: recarregarAtendimento } = useAtendimento(
    currentOrganization?.id
  )
  // SPEC 157: o agente é de atendimento quando tem caixa ligada ou documento no ar.
  const doAgente = atendimentoDe(agentId)
  const atendeAsConversas = doAgente !== null
  const atendimento = doAgente
    ? {
        agent_id: doAgente.agent_id,
        agent_name: doAgente.name,
        blueprint_version: doAgente.published_version,
        blueprint_published_at: null,
      }
    : null

  const {
    currentStep,
    formData,
    stepValidation,
    isSubmitting,
    error,
    setStep,
    setFormData,
    setStepValidation,
    setSubmitting,
    setError,
    reset,
  } = useAgentWizardStore()

  const [showPreview, setShowPreview] = useState(initialShowPlayground)
  const [showPlayground, setShowPlayground] = useState(initialShowPlayground)
  const [isLoadingAgent, setIsLoadingAgent] = useState(editMode)
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null)

  const safeFormData = {
    ...formData,
    tool_ids: Array.isArray(formData.tool_ids) ? formData.tool_ids : [],
    knowledge_base_ids: Array.isArray(formData.knowledge_base_ids)
      ? formData.knowledge_base_ids
      : [],
  }

  const methods = useForm<AgentWizardData>({
    resolver: zodResolver(agentWizardSchema),
    defaultValues: { ...agentWizardDefaults, ...safeFormData },
    mode: 'onChange',
  })

  // Load agent data in edit mode
  useEffect(() => {
    if (editMode && agentId && selectedProject?.id) {
      const loadAgent = async () => {
        setIsLoadingAgent(true)
        try {
          const agent = await agentService.getAgent(selectedProject.id, agentId)
          setOriginalAgent(agent)
          const wizardData = agentToWizardData(agent)
          setFormData(wizardData)
          methods.reset({ ...agentWizardDefaults, ...wizardData })
          // O passo pedido na URL vence o padrão (spec 157: `?step=7` abre o
          // Atendimento). Se o agente não atender, o efeito abaixo recua para 6.
          if (currentStep === 0) setStep(passoPedido >= 1 && passoPedido <= 7 ? passoPedido : 1)
        } catch (err) {
          const axiosError = err as AxiosError<{ detail?: string }>
          setError(axiosError.response?.data?.detail || t('wizard.errors.loadAgent'))
        } finally {
          setIsLoadingAgent(false)
        }
      }
      reset()
      loadAgent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, agentId, selectedProject?.id])

  // Sync form changes to store
  useEffect(() => {
    const subscription = methods.watch((data) => {
      setFormData(data as Partial<AgentWizardData>)
    })
    return () => subscription.unsubscribe()
  }, [methods, setFormData])

  const minStepId = editMode ? 1 : 0
  // O TETO ACOMPANHA O QUE ESTÁ VISÍVEL. Com o teto fixo no último passo, o
  // botão Avançar levaria ao passo Atendimento de um agente que não atende —
  // uma aba que a barra não mostra e que não tem documento para editar.
  const passosVisiveis = WIZARD_STEPS.filter(
    (passo) => passo.id !== 7 || atendeAsConversas
  )
  const maxStepId = passosVisiveis[passosVisiveis.length - 1].id

  useEffect(() => {
    // DESLIGAR A CHAVE COM O PASSO ABERTO deixaria o usuário numa aba que
    // desapareceu da barra. Volta para Avançado, que é o passo anterior.
    if (currentStep === 7 && !atendeAsConversas) setStep(6)
  }, [currentStep, atendeAsConversas, setStep])

  useEffect(() => {
    if (!passoPedido || Number.isNaN(passoPedido) || isLoadingAgent) return
    if (passoPedido === 7 && !atendeAsConversas) return
    if (currentStep !== passoPedido) setStep(passoPedido)
    // só na entrada (depois de o agente carregar): o usuário pode navegar
    // depois sem a URL puxar de volta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passoPedido, atendeAsConversas, isLoadingAgent])

  // Navigate to next step
  const handleNext = useCallback(async () => {
    if (currentStep >= maxStepId) return

    const currentStepConfig = WIZARD_STEPS.find((s) => s.id === currentStep)
    if (currentStep !== 0 && currentStepConfig && !('optional' in currentStepConfig && currentStepConfig.optional)) {
      const fields = [...currentStepConfig.fields] as (keyof AgentWizardData)[]
      const ok = await methods.trigger(fields)
      setStepValidation(currentStep, ok)
      if (!ok) return
    }

    // Save pending new API key before leaving the LLM config step
    if (currentStep === 2) {
      try {
        const { _pendingApiKeySaver } = useAgentWizardStore.getState()
        if (_pendingApiKeySaver) {
          await _pendingApiKeySaver()
        }
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        setError(axiosError.response?.data?.detail || t('wizard.apiKey.saveFailed'))
        return
      }
    }

    setStep(currentStep + 1)
  }, [currentStep, maxStepId, methods, setStep, setStepValidation, setError, t])

  // Navigate to previous step
  const handlePrevious = useCallback(() => {
    if (currentStep > minStepId) {
      setStep(currentStep - 1)
    }
  }, [currentStep, minStepId, setStep])

  // Tab click: validate current tab then navigate freely
  const handleTabClick = useCallback(
    async (step: number) => {
      if (step === currentStep) return
      const cfg = WIZARD_STEPS.find((s) => s.id === currentStep)
      if (cfg && !('optional' in cfg && cfg.optional)) {
        const fields = [...cfg.fields] as (keyof AgentWizardData)[]
        const ok = await methods.trigger(fields)
        setStepValidation(currentStep, ok)
      }
      // Save pending new API key before leaving the LLM config step
      if (currentStep === 2) {
        try {
          const { _pendingApiKeySaver } = useAgentWizardStore.getState()
          if (_pendingApiKeySaver) {
            await _pendingApiKeySaver()
          }
        } catch (err) {
          const axiosError = err as AxiosError<{ detail?: string }>
          setError(axiosError.response?.data?.detail || t('wizard.apiKey.saveFailed'))
          return
        }
      }
      setStep(step)
    },
    [currentStep, methods, setStep, setStepValidation, setError, t]
  )

  // ── Transform helpers ──

  const transformToCreateRequest = (data: AgentWizardData): AgentCreateRequest => ({
    name: data.name,
    description: data.description,
    system_prompt: data.system_prompt,
    llm_config: {
      provider: data.provider ?? undefined,
      model: data.model ?? undefined,
      temperature: data.temperature,
      max_tokens: data.max_tokens,
      api_key_id: data.api_key_id ?? undefined,
    },
    memory_config: {
      enabled: data.memory_enabled,
      type: data.memory_type ?? undefined,
      scope: data.memory_scope ?? undefined,
      context_window: data.memory_context_window,
      retention_days: data.memory_retention_days ?? undefined,
      auto_summarize: data.memory_auto_summarize,
      max_tokens: data.memory_max_tokens ?? undefined,
      window_size: data.memory_window_size ?? undefined,
    },
    settings: {
      timeout: data.timeout,
      max_retries: data.max_retries,
      max_iterations: data.max_iterations,
    },
    is_template: data.is_template,
    is_active: data.is_active,
  })

  const transformToUpdateRequest = (data: AgentWizardData): AgentUpdateRequest => ({
    name: data.name,
    description: data.description,
    system_prompt: data.system_prompt,
    llm_config: {
      provider: data.provider ?? undefined,
      model: data.model ?? undefined,
      temperature: data.temperature,
      max_tokens: data.max_tokens,
      api_key_id: data.api_key_id ?? undefined,
    },
    memory_config: {
      enabled: data.memory_enabled,
      type: data.memory_type ?? undefined,
      scope: data.memory_scope ?? undefined,
      context_window: data.memory_context_window,
      retention_days: data.memory_retention_days ?? undefined,
      auto_summarize: data.memory_auto_summarize,
      max_tokens: data.memory_max_tokens ?? undefined,
      window_size: data.memory_window_size ?? undefined,
    },
    settings: {
      timeout: data.timeout,
      max_retries: data.max_retries,
      max_iterations: data.max_iterations,
    },
    is_template: data.is_template,
    is_active: data.is_active,
  })

  // ── Submit ──

  const handleSubmit = async () => {
    if (!selectedProject?.id) {
      setError(editMode ? t('wizard.errors.selectProject') : t('wizard.errors.selectProjectCreate'))
      return
    }
    if (editMode && !agentId) {
      setError(t('wizard.errors.agentIdNotFound'))
      return
    }

    const isValid = await methods.trigger()
    if (!isValid) {
      for (const step of WIZARD_STEPS) {
        if (editMode && step.id === 0) continue
        const fields = [...step.fields] as (keyof AgentWizardData)[]
        const ok = await methods.trigger(fields)
        setStepValidation(step.id, ok)
        if (!ok) {
          setStep(step.id)
          break
        }
      }
      setError(t('wizard.errors.fixErrors'))
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Save pending new API key before creating/updating the agent
      const { _pendingApiKeySaver } = useAgentWizardStore.getState()
      if (_pendingApiKeySaver) {
        await _pendingApiKeySaver()
      }

      const data = methods.getValues()

      if (editMode && agentId) {
        await agentService.updateAgent(selectedProject.id, agentId, transformToUpdateRequest(data))

        const curTools = data.tool_ids || []
        const origTools = originalAgent?.tools?.map((t) => t.tool_id) || []
        for (const id of origTools) {
          if (!curTools.includes(id)) {
            try { await agentService.detachTool(selectedProject.id, agentId, id) } catch { console.warn(`Failed to detach tool ${id}`) }
          }
        }
        const configs = data.tool_configs || {}
        for (const id of curTools) {
          if (!origTools.includes(id)) {
            try { await agentService.attachTool(selectedProject.id, agentId, { tool_id: id, config: configs[id] }) } catch { console.warn(`Failed to attach tool ${id}`) }
          }
        }

        const curKbs = data.knowledge_base_ids || []
        const origKbs = originalAgent?.knowledge_bases?.map((kb) => kb.knowledge_base_id) || []
        for (const id of origKbs) {
          if (!curKbs.includes(id)) {
            try { await agentService.detachKnowledgeBase(selectedProject.id, agentId, id) } catch { console.warn(`Failed to detach KB ${id}`) }
          }
        }
        for (const id of curKbs) {
          if (!origKbs.includes(id)) {
            try { await agentService.attachKnowledgeBase(selectedProject.id, agentId, { knowledge_base_id: id }) } catch { console.warn(`Failed to attach KB ${id}`) }
          }
        }

        reset()
        onSuccess ? onSuccess(agentId) : navigate('/agents')
      } else {
        const res = await agentService.createAgent(selectedProject.id, transformToCreateRequest(data))
        const newId = res.agent.id

        if (data.tool_ids?.length) {
          const configs = data.tool_configs || {}
          for (const id of data.tool_ids) {
            try { await agentService.attachTool(selectedProject.id, newId, { tool_id: id, config: configs[id] }) } catch { console.warn(`Failed to attach tool ${id}`) }
          }
        }
        if (data.knowledge_base_ids?.length) {
          for (const id of data.knowledge_base_ids) {
            try { await agentService.attachKnowledgeBase(selectedProject.id, newId, { knowledge_base_id: id }) } catch { console.warn(`Failed to attach KB ${id}`) }
          }
        }

        reset()
        onSuccess ? onSuccess(newId) : navigate('/agents')
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ detail?: string }>
      setError(axiosError.response?.data?.detail || (editMode ? t('wizard.errors.updateFailed') : t('wizard.errors.createFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => (onCancel ? onCancel() : navigate('/agents'))

  const renderStep = () => {
    switch (currentStep) {
      case 0: return <StepTemplateSelect />
      case 1: return (
        <StepBasicInfo
          organizationId={currentOrganization?.id}
          agentId={agentId}
          atendimento={atendimento}
          aoMudarAtendimento={recarregarAtendimento}
        />
      )
      case 2: return <StepLLMConfig />
      case 3: return <StepSystemPrompt />
      case 4: return <StepTools />
      case 5: return <StepKnowledge />
      case 6: return <StepAdvanced editMode={editMode} agentId={agentId} />
      case 7: return (
        <StepAtendimento
          organizationId={currentOrganization?.id}
          organizationName={currentOrganization?.name}
          agentId={agentId}
        />
      )
      default: return null
    }
  }

  /* ─── Loading ─── */

  if (isLoadingAgent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
            <div className="absolute inset-0 rounded-2xl animate-pulse-glow" />
          </div>
          <p className="text-gray-400 text-sm">{t('wizard.loading.agent')}</p>
        </div>
      </div>
    )
  }

  /* ─── Render ─── */

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-[calc(100vh-13rem)] min-h-[500px]">
        {/* ── Mobile/Tablet: Horizontal tabs ── */}
        <div className="lg:hidden mb-4">
          <WizardTabs
            currentStep={currentStep}
            stepValidation={stepValidation}
            onStepClick={handleTabClick}
            editMode={editMode}
            orientation="horizontal"
            mostrarAtendimento={atendeAsConversas}
          />
        </div>

        {/* ── Main 3-panel layout ── */}
        <div className="flex flex-1 gap-5 min-h-0">
          {/* Desktop: Vertical tab sidebar */}
          <div className="hidden lg:flex lg:flex-col py-4 pr-1">
            <WizardTabs
              currentStep={currentStep}
              stepValidation={stepValidation}
              onStepClick={handleTabClick}
              editMode={editMode}
              orientation="vertical"
              mostrarAtendimento={atendeAsConversas}
            />
          </div>

          {/* Center: Content */}
          <div className="flex-1 flex flex-col min-w-0">
            <Card variant="glass" className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-gray-800/40">
                <h2 className="text-lg font-semibold text-white">
                  {t(`wizard.stepsConfig.${currentStep}.title`)}
                </h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {t(`wizard.stepsConfig.${currentStep}.description`)}
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="mx-5 sm:mx-6 mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                  <X className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1">{error}</span>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="text-red-400/60 hover:text-red-400"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Content */}
              <div
                key={currentStep}
                className="flex-1 overflow-y-auto p-5 sm:p-6 scrollbar-thin min-h-0"
              >
                <div className="animate-tab-content">{renderStep()}</div>
              </div>
            </Card>

            {/* Action bar */}
            {/* **`justify-between` SEM QUEBRA CORTAVA O BOTÃO PRINCIPAL.** Em
                375px, Cancelar + olho + Avançar + Salvar não cabem numa linha,
                e o excedente saía por baixo do recorte do painel: "Salvar Age"
                era o que se via. Com `flex-wrap` o grupo da direita desce uma
                linha inteiro, e o `ml-auto` mantém as ações no canto certo
                mesmo quando ele fica sozinho na linha. */}
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-white"
                >
                  {t('wizard.navigation.cancel')}
                </Button>

                {currentStep > minStepId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handlePrevious}
                    className="border-gray-700/50 text-gray-300 hover:text-white hover:border-gray-600"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1.5" />
                    {t('wizard.navigation.previous')}
                  </Button>
                )}
              </div>

              <div className="ml-auto flex items-center gap-2 sm:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="xl:hidden border-gray-700/50 text-gray-400 hover:text-white hover:border-gray-600"
                >
                  <Eye className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">{t('wizard.panels.preview')}</span>
                </Button>

                {currentStep < maxStepId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    className="border-indigo-500/50 text-indigo-300 hover:text-white hover:border-indigo-400 hover:bg-indigo-500/10"
                  >
                    {t('wizard.navigation.advance')}
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                )}

                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isLoadingAgent}
                  className={cn(
                    'bg-indigo-500 hover:bg-indigo-600 text-white min-w-[120px] transition-all',
                    isSubmitting && 'opacity-80'
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editMode
                        ? t('wizard.navigation.saving')
                        : t('wizard.navigation.creating')}
                    </>
                  ) : (
                    <>
                      {editMode ? (
                        <Save className="h-4 w-4 mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      {editMode
                        ? t('wizard.navigation.saveAgent')
                        : t('wizard.navigation.createAgent')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: Preview/Playground */}
          <Card
            variant="glass"
            className="hidden xl:flex xl:flex-col xl:w-80 2xl:w-96 flex-shrink-0 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-800/40 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                {showPlayground ? (
                  <>
                    <Play className="h-4 w-4 text-emerald-400" />
                    {t('wizard.panels.playground')}
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 text-indigo-400" />
                    {t('wizard.panels.preview')}
                  </>
                )}
              </h3>
              {editMode && agentId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPlayground(!showPlayground)}
                  className="text-gray-400 hover:text-white text-xs h-7 px-2"
                >
                  {showPlayground ? (
                    <>
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      {t('wizard.panels.preview')}
                    </>
                  ) : (
                    <>
                      <Play className="h-3.5 w-3.5 mr-1" />
                      {t('wizard.panels.test')}
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {showPlayground && editMode && agentId && selectedProject?.id ? (
                <AgentPlayground
                  projectId={selectedProject.id}
                  agentId={agentId}
                  className="h-full"
                />
              ) : (
                <div className="p-4">
                  <AgentPreview />
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* ── Mobile Preview Modal ── */}
      {showPreview && (
        <div className="fixed inset-0 z-[100] xl:hidden flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          />
          <div
            className="relative w-full max-w-[500px] lg:max-w-[600px] max-h-[85vh] flex flex-col rounded-2xl border border-gray-700/50 animate-fade-in"
            style={{ backgroundColor: '#0d0d14' }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800/40">
              <h3 className="text-base font-medium text-white flex items-center gap-2">
                {showPlayground ? (
                  <><Play className="h-4 w-4 text-emerald-400" />{t('wizard.panels.playground')}</>
                ) : (
                  <><Eye className="h-4 w-4 text-indigo-400" />{t('wizard.panels.preview')}</>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {editMode && agentId && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPlayground(!showPlayground)} className="text-gray-400 hover:text-white">
                    {showPlayground ? (<><Eye className="h-4 w-4 mr-1" />{t('wizard.panels.preview')}</>) : (<><Play className="h-4 w-4 mr-1" />{t('wizard.panels.test')}</>)}
                  </Button>
                )}
                <Button type="button" variant="ghost" size="icon" onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-white h-8 w-8">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {showPlayground && editMode && agentId && selectedProject?.id ? (
                <AgentPlayground projectId={selectedProject.id} agentId={agentId} className="h-full min-h-[400px]" />
              ) : (
                <div className="p-5"><AgentPreview /></div>
              )}
            </div>
          </div>
        </div>
      )}
    </FormProvider>
  )
}
